import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle, MapPin, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";

/**
 * Reusable SEO landing page template — long-form, keyword-dense,
 * built to rank on a single exact-match keyword (e.g. "event registration belgium").
 *
 * Props:
 *  - keyword       (string) exact target keyword (used in H1, title, breadcrumbs)
 *  - path          (string) URL path of the page
 *  - title         (string) <title> tag (~60 chars, contains keyword)
 *  - description   (string) meta description (~150 chars, contains keyword)
 *  - hero          { eyebrow, headline (JSX), subhead }
 *  - intro         (array of strings) opening paragraphs (long-form)
 *  - benefits      (array of {title, body}) 3-6 key benefits
 *  - features      (array of strings) bullet feature list
 *  - useCases      (array of {title, body})
 *  - faqs          (array of {q, a}) for FAQ schema
 *  - relatedProducts (array of {slug, name}) internal cross-links
 *  - serviceType   (string) Schema.org Service.serviceType value
 */
export default function SeoLandingTemplate({
  keyword,
  path,
  title,
  description,
  hero,
  intro = [],
  benefits = [],
  features = [],
  useCases = [],
  faqs = [],
  relatedProducts = [],
  serviceType,
}) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://onetec.eu";

  const schemaGraph = [
    {
      "@type": "Service",
      name: keyword,
      serviceType: serviceType || keyword,
      provider: {
        "@type": "Organization",
        name: "OneTec",
        url: origin,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Tollaan 75",
          addressLocality: "Zaventem",
          postalCode: "1932",
          addressCountry: "BE",
        },
      },
      areaServed: [
        { "@type": "Country", name: "Belgium" },
        { "@type": "Country", name: "Netherlands" },
        { "@type": "Country", name: "France" },
        { "@type": "Country", name: "Luxembourg" },
      ],
      description,
      url: `${origin}${path}`,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: `${origin}/services`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: keyword,
          item: `${origin}${path}`,
        },
      ],
    },
  ];

  if (faqs.length) {
    schemaGraph.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <div data-testid={`landing-${path.replace(/\//g, "")}`}>
      <Seo
        title={title}
        path={path}
        description={description}
        noTemplate
        schema={{ "@context": "https://schema.org", "@graph": schemaGraph }}
      />

      {/* HERO */}
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16">
          {/* Breadcrumbs (visible + good for SEO) */}
          <nav
            aria-label="Breadcrumb"
            className="text-xs uppercase tracking-widest font-bold text-ot-fg/60 mb-6"
          >
            <Link to="/" className="hover:text-ot-indigo">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-ot-indigo">
              Services
            </Link>
            <span className="mx-2">/</span>
            <span className="text-ot-indigo">{keyword}</span>
          </nav>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-widest font-bold text-ot-indigo"
          >
            {hero.eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.92] mt-3"
          >
            {hero.headline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 text-lg md:text-xl text-ot-fg/80 max-w-3xl"
          >
            {hero.subhead}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link to="/contact" className="nb-btn" data-testid="landing-cta-primary">
              Get a quote <ArrowUpRight weight="bold" />
            </Link>
            <Link to="/products" className="nb-btn nb-btn-ghost">
              Browse products <ArrowUpRight weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* INTRO + BENEFITS */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-5 text-lg leading-relaxed text-ot-fg/85">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
            Overview
          </p>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-tight !mt-2">
            Why teams in Belgium choose OneTec for {keyword.toLowerCase()}.
          </h2>
          {intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <aside className="lg:col-span-5">
          <div className="nb-card p-6 sticky top-24">
            <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
              Belgium HQ · EU delivery
            </p>
            <h3 className="font-display text-2xl uppercase tracking-tighter mt-2">
              Talk to a specialist
            </h3>
            <p className="text-sm text-ot-fg/70 mt-2">
              15-person team in Zaventem. 525 events a year. We reply within 24h.
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} weight="bold" className="mt-0.5 shrink-0" />
                <span>Tollaan 75, 1932 Zaventem, Belgium</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} weight="bold" />
                <a href="tel:+3227023636" className="hover:text-ot-indigo">
                  +32 2 702 36 36
                </a>
              </li>
              <li className="flex items-center gap-3">
                <EnvelopeSimple size={18} weight="bold" />
                <a href="mailto:hello@onetec.eu" className="hover:text-ot-indigo">
                  hello@onetec.eu
                </a>
              </li>
            </ul>
            <Link to="/contact" className="nb-btn w-full justify-center mt-5">
              Start a project <ArrowUpRight weight="bold" />
            </Link>
          </div>
        </aside>
      </section>

      {/* BENEFITS GRID */}
      {benefits.length > 0 && (
        <section className="bg-ot-bg border-y-2 border-ot-fg">
          <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">
            <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
              What you get
            </p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter mt-2 leading-tight">
              Built for Belgian events.
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="nb-card p-6"
                >
                  <span className="font-display text-3xl text-ot-indigo">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl uppercase tracking-tight mt-2">
                    {b.title}
                  </h3>
                  <p className="text-ot-fg/75 mt-2">{b.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURE CHECKLIST */}
      {features.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
            Capabilities
          </p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter mt-2 leading-tight">
            Everything included.
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-4">
            {features.map((f) => (
              <li key={f} className="flex gap-3 items-start nb-card p-4 bg-white">
                <CheckCircle
                  size={22}
                  weight="fill"
                  className="text-ot-indigo mt-0.5 shrink-0"
                />
                <span className="text-base">{f}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* USE CASES */}
      {useCases.length > 0 && (
        <section className="bg-ot-indigo text-white border-y-2 border-ot-fg">
          <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">
            <p className="text-xs uppercase tracking-widest font-bold text-ot-peach">
              In the wild
            </p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter mt-2 leading-tight">
              Where we shine.
            </h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u) => (
                <div
                  key={u.title}
                  className="border-2 border-white/30 p-5 hover:bg-ot-peach hover:text-ot-fg hover:border-ot-peach transition-colors"
                >
                  <h3 className="font-display text-lg uppercase tracking-tight">
                    {u.title}
                  </h3>
                  <p className="text-sm text-white/80 mt-2 group-hover:text-ot-fg">
                    {u.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED PRODUCTS */}
      {relatedProducts.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
            Related products
          </p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter mt-2 leading-tight">
            Pick your product.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {relatedProducts.map((rp) => (
              <Link
                key={rp.slug}
                to={`/products/${rp.slug}`}
                className="nb-card p-5 group"
              >
                <span className="text-xs uppercase tracking-widest text-ot-indigo font-bold">
                  Product
                </span>
                <h3 className="font-display text-lg uppercase tracking-tight mt-2 leading-tight">
                  {rp.name}
                </h3>
                <span className="inline-flex items-center gap-1 text-sm font-display uppercase mt-3 group-hover:text-ot-indigo">
                  See product{" "}
                  <ArrowUpRight
                    weight="bold"
                    className="group-hover:rotate-45 transition-transform"
                  />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-ot-bg border-t-2 border-ot-fg">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-20">
            <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">
              FAQ
            </p>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter mt-2 leading-tight">
              Frequently asked.
            </h2>
            <div className="mt-10 space-y-4">
              {faqs.map((f) => (
                <details
                  key={f.q}
                  className="nb-card p-5 bg-white open:shadow-[10px_10px_0px_var(--ot-indigo)]"
                >
                  <summary className="font-display text-lg uppercase tracking-tight cursor-pointer list-none flex justify-between items-center gap-3">
                    <span>{f.q}</span>
                    <span
                      aria-hidden
                      className="text-ot-indigo text-2xl leading-none"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-ot-fg/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16">
        <div className="bg-ot-peach border-2 border-ot-fg p-10 md:p-16 text-center shadow-[12px_12px_0px_#111111]">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.92]">
            Ready to brief us on {keyword.toLowerCase()}?
          </h2>
          <p className="mt-5 text-lg max-w-xl mx-auto">
            15-person team in Zaventem. We reply within 24 hours with a first read.
          </p>
          <Link to="/contact" className="nb-btn mt-7" data-testid="landing-cta-bottom">
            Start a project <ArrowUpRight weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
